import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import {
  getMe,
  updateProfileDetails,
  updateProfilePassword,
  UPDATE_USER,
  UPDATE_PASSWORD,
  resetPayload
} from "store/actions/userAction";
import Modal from "components/Modal";
import Textbox from "components/Forms/Elements/Textbox";
import SubmitButton from "components/Forms/Buttons/Submit";
import Notification, { notifyApiError } from "components/Notification";
import Translate from "helpers/Translate";

function Profile({ showProfile, setShowProfile }) {
  const dispatch = useDispatch();
  const user = useSelector(state => state.user.getMe);
  const error = useSelector(state => state.user.errorProfile);
  const isLoadingUser = useSelector(state => state.user.isLoadingUser);
  const updateUserProfileResult = useSelector(
    state => state.user.updateProfileDetails
  );
  const updateProfilePasswordResult = useSelector(
    state => state.user.updateProfilePassword
  );

  const [currentTab, setCurrentTab] = useState("basic");
  const [profile, setProfile] = useState({
    fullname: "",
    email: "",
    oldPassword: "",
    password: "",
    password_confirmation: ""
  });

  // Get the current user details
  useEffect(() => {
    if (!user) {
      dispatch(getMe());
    } else {
      const newProfile = { ...profile };
      newProfile.fullname = user.fullname;
      newProfile.email = user.email;
      setProfile(newProfile);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, dispatch]);

  // Update profile success notification
  useEffect(() => {
    if (updateUserProfileResult) {
      Notification.success(updateUserProfileResult);
      setShowProfile(false);
      dispatch(resetPayload(UPDATE_USER));
      dispatch(getMe());
    }
  }, [updateUserProfileResult, setShowProfile, dispatch]);

  // Update password success notification
  useEffect(() => {
    if (updateProfilePasswordResult) {
      Notification.success(updateProfilePasswordResult);
      setShowProfile(false);
      dispatch(resetPayload(UPDATE_PASSWORD));
    }
  }, [updateProfilePasswordResult, setShowProfile, dispatch]);

  // Any errors, notify
  useEffect(() => {
    if (error) {
      notifyApiError(error);
    }
  }, [error, dispatch]);

  const handleChange = e => {
    const newProfile = { ...profile };
    newProfile[e.target.name] = e.target.value;
    setProfile(newProfile);
  };

  const handleUpdateProfile = () => {
    dispatch(
      updateProfileDetails({
        fullname: profile.fullname,
        email: profile.email
      })
    );
  };

  const handleChangePassword = () => {
    dispatch(
      updateProfilePassword({
        oldPassword: profile.oldPassword,
        password: profile.password,
        password_confirmation: profile.password_confirmation
      })
    );
  };

  return (
    <Modal isOpen={showProfile} width="305px" height="435px" overflow="hidden">
      <div className="edit-profile">
        <h2>
          {Translate({ id: "edit_profile" })}
          <i
            className="far fa-times-circle pointer fr"
            onClick={() => setShowProfile(false)}
          ></i>
        </h2>

        <div className="tab-wrapper mt-30">
          <div
            className={classNames("tab tab-basic mr-30 ", {
              selected: currentTab === "basic"
            })}
            onClick={() => setCurrentTab("basic")}
          >
            {Translate({ id: "basic_details" })}
          </div>
          <div
            className={classNames("tab tab-password", {
              selected: currentTab === "password"
            })}
            onClick={() => setCurrentTab("password")}
          >
            {Translate({ id: "change_password" })}
          </div>
        </div>

        {/* Basic Details */}
        <div
          className={classNames("basic-details mt-50 ", {
            hide: currentTab === "password"
          })}
        >
          <div className="form-group">
            <label htmlFor="fullname">{Translate({ id: "fullname" })}:</label>
            <Textbox
              id="fullname"
              type="text"
              name="fullname"
              errorKey="fullname"
              fieldValue={profile.fullname}
              mandatory={true}
              errors={error}
              onReturnKey={handleUpdateProfile}
              onChange={handleChange}
            />
          </div>
          <div className="form-group mt-10">
            <label htmlFor="email">{Translate({ id: "email" })}:</label>
            <Textbox
              id="email"
              type="email"
              name="email"
              errorKey="email"
              fieldValue={profile.email}
              mandatory={true}
              errors={error}
              onReturnKey={handleUpdateProfile}
              onChange={handleChange}
            />
          </div>
          <SubmitButton
            disabled={false}
            className="btn btn-primary mt-30 fr"
            buttonText={Translate({ id: "update_details" })}
            btnId="send"
            isLoading={isLoadingUser}
            handleSubmit={handleUpdateProfile}
          />
        </div>

        {/* Change Password */}
        <div
          className={classNames("change-password mt-40  ", {
            hide: currentTab === "basic"
          })}
        >
          <div className="form-group">
            <label htmlFor="fullname">
              {Translate({ id: "old_password" })} :
            </label>
            <Textbox
              id="oldPassword"
              type="password"
              name="oldPassword"
              errorKey="oldPassword"
              fieldValue={profile.oldPassword}
              mandatory={true}
              errors={error}
              onReturnKey={handleChangePassword}
              onChange={handleChange}
            />
          </div>
          <div className="form-group mt-10">
            <label htmlFor="password">
              {Translate({ id: "new_password" })}:
            </label>
            <Textbox
              id="password"
              type="password"
              name="password"
              errorKey="password"
              fieldValue={profile.password}
              mandatory={true}
              errors={error}
              onReturnKey={handleChangePassword}
              onChange={handleChange}
            />
          </div>
          <div className="form-group mt-10">
            <label htmlFor="retype-password">
              {Translate({ id: "retype_password" })}:
            </label>
            <Textbox
              id="password_confirmation"
              type="password"
              name="password_confirmation"
              errorKey="password_confirmation"
              fieldValue={profile.password_confirmation}
              mandatory={true}
              errors={error}
              onReturnKey={handleChangePassword}
              onChange={handleChange}
            />
          </div>
          <button className=""></button>
          <SubmitButton
            disabled={false}
            className="btn btn-primary mt-30 fr"
            buttonText={Translate({ id: "update_password" })}
            btnId="send"
            isLoading={isLoadingUser}
            handleSubmit={handleChangePassword}
          />
        </div>
      </div>
    </Modal>
  );
}

export default Profile;
